import React, { useState } from 'react';
import AppHeader from '../../../components/AppHeader.tsx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faAward,
  faCrown,
  faDownload,
  faEdit,
  faEuroSign,
  faFilter,
  faGem,
  faGift,
  faMedal,
  faPercent,
  faPlus,
  faSearch,
  faStar,
  faTrophy,
  faUser,
  faUsers,
} from '@fortawesome/free-solid-svg-icons';

const LoyaltyProgramPage: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterTier, setFilterTier] = useState('all');

  const breadcrumb = [{ label: 'Zákazníci' }, { label: 'Loajální program' }];

  const loyaltyTiers = [
    {
      id: 'bronze',
      name: 'Bronzová',
      icon: faMedal,
      color: 'text-orange-600',
      bgColor: 'bg-orange-50',
      minSpent: 0,
      maxSpent: 4999,
      discount: 5,
      benefits: ['5% sleva na všechny služby', 'Připomínky o rezervacích'],
    },
    {
      id: 'silver',
      name: 'Stříbrná',
      icon: faAward,
      color: 'text-gray-600',
      bgColor: 'bg-gray-50',
      minSpent: 5000,
      maxSpent: 14999,
      discount: 10,
      benefits: ['10% sleva na všechny služby', 'Připomínky o rezervacích', 'Prioritní rezervace'],
    },
    {
      id: 'gold',
      name: 'Zlatá',
      icon: faCrown,
      color: 'text-yellow-600',
      bgColor: 'bg-yellow-50',
      minSpent: 15000,
      maxSpent: 29999,
      discount: 15,
      benefits: ['15% sleva na všechny služby', 'Připomínky o rezervacích', 'Prioritní rezervace', 'Dárkový balíček k narozeninám'],
    },
    {
      id: 'platinum',
      name: 'Platinová',
      icon: faGem,
      color: 'text-purple-600',
      bgColor: 'bg-purple-50',
      minSpent: 30000,
      maxSpent: 999999,
      discount: 20,
      benefits: ['20% sleva na všechny služby', 'Připomínky o rezervacích', 'Prioritní rezervace', 'Dárkový balíček k narozeninám', 'Osobní konzultace zdarma'],
    },
  ];

  const customers = [
    {
      id: '1',
      name: 'Jana Nováková',
      email: 'jana.novakova@email.cz',
      phone: '+420 123 456 789',
      totalSpent: 18500,
      visits: 15,
      tier: 'gold',
      points: 1850,
      joinDate: '2023-01-15',
      lastVisit: '2024-11-15',
    },
    {
      id: '2',
      name: 'Petr Svoboda',
      email: 'petr.svoboda@email.cz',
      phone: '+420 987 654 321',
      totalSpent: 8500,
      visits: 8,
      tier: 'silver',
      points: 850,
      joinDate: '2023-06-20',
      lastVisit: '2024-11-10',
    },
    {
      id: '3',
      name: 'Marie Krásná',
      email: 'marie.krasna@email.cz',
      phone: '+420 555 666 777',
      totalSpent: 42000,
      visits: 28,
      tier: 'platinum',
      points: 4200,
      joinDate: '2022-03-10',
      lastVisit: '2024-11-14',
    },
    {
      id: '4',
      name: 'Alena Světlá',
      email: 'alena.svetla@email.cz',
      phone: '+420 444 333 222',
      totalSpent: 2800,
      visits: 3,
      tier: 'bronze',
      points: 280,
      joinDate: '2024-08-05',
      lastVisit: '2024-10-20',
    },
    {
      id: '5',
      name: 'Lukáš Černý',
      email: 'lukas.cerny@email.cz',
      phone: '+420 777 888 999',
      totalSpent: 7200,
      visits: 6,
      tier: 'silver',
      points: 720,
      joinDate: '2024-02-12',
      lastVisit: '2024-11-12',
    },
  ];

  const filteredCustomers = customers.filter(customer => {
    const matchesSearch = customer.name.toLowerCase().includes(searchTerm.toLowerCase()) || customer.email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesTier = filterTier === 'all' || customer.tier === filterTier;
    return matchesSearch && matchesTier;
  });

  const getTierInfo = (tierId: string) => {
    return loyaltyTiers.find(tier => tier.id === tierId) || loyaltyTiers[0];
  };

  const getTierIcon = (tierId: string) => {
    const tier = getTierInfo(tierId);
    return tier.icon;
  };

  const getTierColor = (tierId: string) => {
    const tier = getTierInfo(tierId);
    return tier.color;
  };

  const getTierName = (tierId: string) => {
    const tier = getTierInfo(tierId);
    return tier.name;
  };

  const totalCustomers = customers.length;
  const totalPoints = customers.reduce((sum, c) => sum + c.points, 0);
  const totalSpent = customers.reduce((sum, c) => sum + c.totalSpent, 0);
  const averageSpent = totalSpent / totalCustomers;

  return (
    <div className="h-full flex flex-col">
      <AppHeader title="Loajální program" breadcrumb={breadcrumb} />

      <div className="flex-1 overflow-y-auto bg-gray-50">
        <div className="p-6 space-y-6">
          {/* Header */}
          <section className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-semibold text-gray-900">Loajální program</h1>
                <p className="text-gray-600 mt-1">Spravujte program věrnosti zákazníků</p>
              </div>
              <div className="flex items-center space-x-3">
                <button className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors">
                  <FontAwesomeIcon icon={faPlus} className="mr-2" />
                  Nový program
                </button>
                <button className="border border-gray-300 px-4 py-2 rounded-lg hover:bg-gray-50 transition-colors">
                  <FontAwesomeIcon icon={faDownload} className="mr-2" />
                  Export
                </button>
              </div>
            </div>
          </section>

          {/* Stats Cards */}
          <section className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center">
                <div className="p-3 bg-blue-50 rounded-lg">
                  <FontAwesomeIcon icon={faUsers} className="text-xl text-blue-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Celkem členů</p>
                  <p className="text-2xl font-bold text-gray-900">{totalCustomers}</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center">
                <div className="p-3 bg-green-50 rounded-lg">
                  <FontAwesomeIcon icon={faStar} className="text-xl text-green-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Celkem bodů</p>
                  <p className="text-2xl font-bold text-gray-900">{totalPoints.toLocaleString()}</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center">
                <div className="p-3 bg-purple-50 rounded-lg">
                  <FontAwesomeIcon icon={faEuroSign} className="text-xl text-purple-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Celkem utraceno</p>
                  <p className="text-2xl font-bold text-gray-900">{totalSpent.toLocaleString()} Kč</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center">
                <div className="p-3 bg-orange-50 rounded-lg">
                  <FontAwesomeIcon icon={faTrophy} className="text-xl text-orange-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Průměr na zákazníka</p>
                  <p className="text-2xl font-bold text-gray-900">{Math.round(averageSpent).toLocaleString()} Kč</p>
                </div>
              </div>
            </div>
          </section>

          {/* Loyalty Tiers */}
          <section className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">Úrovně programu</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {loyaltyTiers.map(tier => (
                <div key={tier.id} className={`${tier.bgColor} rounded-lg p-6`}>
                  <div className="flex items-center space-x-3 mb-4">
                    <FontAwesomeIcon icon={tier.icon} className={`text-2xl ${tier.color}`} />
                    <div>
                      <h3 className="font-semibold text-gray-900">{tier.name}</h3>
                      <p className="text-sm text-gray-600">
                        {tier.minSpent.toLocaleString()} - {tier.maxSpent === 999999 ? '∞' : tier.maxSpent.toLocaleString()} Kč
                      </p>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center text-sm">
                      <FontAwesomeIcon icon={faPercent} className="w-4 h-4 mr-2 text-gray-500" />
                      <span className="font-medium">{tier.discount}% sleva</span>
                    </div>
                    <div className="text-sm text-gray-600">{customers.filter(c => c.tier === tier.id).length} členů</div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Search and Filters */}
          <section className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center space-x-4">
              <div className="flex-1 relative">
                <FontAwesomeIcon icon={faSearch} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Hledat členy programu..."
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500/20 focus:border-green-500"
                />
              </div>
              <select
                value={filterTier}
                onChange={e => setFilterTier(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500/20 focus:border-green-500"
              >
                <option value="all">Všechny úrovně</option>
                {loyaltyTiers.map(tier => (
                  <option key={tier.id} value={tier.id}>
                    {tier.name}
                  </option>
                ))}
              </select>
              <button className="flex items-center px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">
                <FontAwesomeIcon icon={faFilter} className="w-4 h-4 mr-2" />
                Více filtrů
              </button>
            </div>
          </section>

          {/* Customers Table */}
          <section className="bg-white rounded-lg shadow-sm">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Zákazník</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Úroveň</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Body</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Celkem utraceno</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Návštěvy</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Sleva</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Akce</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredCustomers.map(customer => {
                    const tierInfo = getTierInfo(customer.tier);
                    return (
                      <tr key={customer.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                              <FontAwesomeIcon icon={faUser} className="w-5 h-5 text-green-600" />
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-gray-900">{customer.name}</div>
                              <div className="text-sm text-gray-500">{customer.email}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <FontAwesomeIcon icon={getTierIcon(customer.tier)} className={`w-5 h-5 mr-2 ${getTierColor(customer.tier)}`} />
                            <span className="text-sm font-medium text-gray-900">{getTierName(customer.tier)}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          <div className="flex items-center">
                            <FontAwesomeIcon icon={faStar} className="w-4 h-4 mr-1 text-yellow-500" />
                            {customer.points.toLocaleString()}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{customer.totalSpent.toLocaleString()} Kč</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{customer.visits}</td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            <FontAwesomeIcon icon={faPercent} className="w-3 h-3 mr-1" />
                            {tierInfo.discount}%
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex items-center space-x-2">
                            <button className="text-blue-600 hover:text-blue-900 p-1" title="Zobrazit detail">
                              <FontAwesomeIcon icon={faUser} className="w-4 h-4" />
                            </button>
                            <button className="text-gray-600 hover:text-gray-900 p-1" title="Upravit">
                              <FontAwesomeIcon icon={faEdit} className="w-4 h-4" />
                            </button>
                            <button className="text-green-600 hover:text-green-900 p-1" title="Dárky">
                              <FontAwesomeIcon icon={faGift} className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </section>

          {/* Quick Actions */}
          <section className="bg-white rounded-lg shadow-sm p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Rychlé akce</h3>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <button className="flex items-center justify-center p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-green-500 hover:bg-green-50 transition-colors">
                <FontAwesomeIcon icon={faGift} className="w-6 h-6 text-gray-400 mr-3" />
                <span className="text-gray-600">Odeslat dárky</span>
              </button>
              <button className="flex items-center justify-center p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors">
                <FontAwesomeIcon icon={faStar} className="w-6 h-6 text-gray-400 mr-3" />
                <span className="text-gray-600">Přidat body</span>
              </button>
              <button className="flex items-center justify-center p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-purple-500 hover:bg-purple-50 transition-colors">
                <FontAwesomeIcon icon={faTrophy} className="w-6 h-6 text-gray-400 mr-3" />
                <span className="text-gray-600">Povýšit úroveň</span>
              </button>
              <button className="flex items-center justify-center p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-orange-500 hover:bg-orange-50 transition-colors">
                <FontAwesomeIcon icon={faDownload} className="w-6 h-6 text-gray-400 mr-3" />
                <span className="text-gray-600">Export dat</span>
              </button>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default LoyaltyProgramPage;
